/**
 * CustomAlert Component
 *
 * A simple wrapper around Ant Design's Alert component
 * that allows you to easily display alerts of types:
 * 'success', 'info', 'warning', or 'error'.
 *
 * Props:
 *  - type: Determines the alert style and icon (required).
 *  - message: The main alert message text (required).
 *  - description: Optional additional description text.
 *  - showIcon: Whether to display the alert icon (default: true).
 *  - closable: Whether the alert can be closed by the user (default: false).
 *
 * Usage example:
 *
 * <CustomAlert
 *    type="warning"
 *    message="Warning"
 *    description="This is a warning alert."
 *    showIcon={true}
 *    closable={true}
 * />
 *
 * This component forwards all props to Ant Design's Alert.
 */

import React from 'react';
import { Alert } from 'antd';

type CustomAlertProps = {
    type: 'success' | 'info' | 'warning' | 'error';
    message: string;
    description?: string;
    showIcon?: boolean;
    closable?: boolean;
};

const CustomAlert: React.FC<CustomAlertProps> = ({ type, message, description, showIcon = true, closable = false }) => {
    return <Alert type={type} message={message} description={description} showIcon={showIcon} closable={closable} />;
};

export default CustomAlert;
